// ============================================================================
// cetliView.js — a CETLI felületei (2026-10-07, tagi kérésre): a felugró ablak, amiben
// elolvasod és eldobod, és az író ablak, amivel küldesz.
//
// Önálló rétegek a <body>-n (mint a keresesView.js / kepNezo.js): a chat újrarajzolásai
// nem zavarják, és a chat render-gyorsítótárához nem kell hozzányúlni.
//
// A cetli szövege MINDIG textContent-tel kerül a felületre (soha nem innerHTML): a
// tartalom bármi lehet. Sima szöveg, sortöréssel - nincs Markdown (egy cetlin nincs rá szükség).
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { feladoNev } from "./chatView.js";
import { log } from "./log.js";

export const CETLI_MAX_KARAKTER = 300;

function datum(ms, uiLang) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(localeFor(uiLang), { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return "";
  }
}

function elem(tag, osztaly, szoveg) {
  const n = document.createElement(tag);
  if (osztaly) n.className = osztaly;
  if (szoveg !== undefined) n.textContent = szoveg;
  return n;
}

// ---------------------------------------------------------------------------
// OLVASÓ / ELDOBÓ FELUGRÓ
// ---------------------------------------------------------------------------

let olvaso = null;

export function cetliOlvasoNyitva() {
  return olvaso !== null;
}

export function zarjBeCetliOlvasot() {
  if (olvaso) olvaso.bezar();
}

/**
 * Megnyitja a cetli-olvasót, vagy - ha már nyitva van - frissíti a tartalmát (új cetli érkezett,
 * vagy egyet eldobtak). A kijelölt cetli azonosító szerint marad meg, ha még létezik.
 *
 * @param {object} p
 * @param {string} p.uiLang
 * @param {string} p.tartalomNyelv a tag chat-nyelve (a küldő nevének írásmódjához)
 * @param {object[]} p.cetlik legújabb elöl (noteData.megjelenitendoCetlik)
 * @param {(noteId: string) => Promise<void>} p.onDobd eldobás (véglegesen töröl)
 * @param {string} [p.kijelolt] melyik cetlivel nyíljon (alap: a legújabb)
 */
export function nyissCetliOlvasot(p) {
  if (olvaso) {
    olvaso.frissit(p);
    return olvaso;
  }

  const gyoker = elem("div", "cetli-hatter");
  gyoker.setAttribute("role", "dialog");
  const kartya = elem("div", "cetli-kartya");
  gyoker.appendChild(kartya);

  let allapot = { ...p, index: 0, eredetiMutat: false, foglalt: false, hiba: null };

  function keres(noteId) {
    return allapot.cetlik.findIndex((c) => c.noteId === noteId);
  }

  function rajzol() {
    const { uiLang, tartalomNyelv, cetlik } = allapot;
    if (cetlik.length === 0) { bezar(); return; }
    if (allapot.index >= cetlik.length) allapot.index = cetlik.length - 1;
    if (allapot.index < 0) allapot.index = 0;
    const c = cetlik[allapot.index];
    kartya.textContent = "";

    const fej = elem("div", "cetli-fej");
    fej.appendChild(elem("div", "cetli-cim", t(uiLang, "note.title")));
    if (cetlik.length > 1) {
      const lapozo = elem("div", "cetli-lapozo");
      const elozo = elem("button", "cetli-lapozo-gomb", "‹");
      elozo.type = "button";
      elozo.setAttribute("aria-label", "‹");
      elozo.disabled = allapot.index === 0;
      elozo.addEventListener("click", () => { allapot.index--; allapot.eredetiMutat = false; rajzol(); });
      const kovetkezo = elem("button", "cetli-lapozo-gomb", "›");
      kovetkezo.type = "button";
      kovetkezo.setAttribute("aria-label", "›");
      kovetkezo.disabled = allapot.index === cetlik.length - 1;
      kovetkezo.addEventListener("click", () => { allapot.index++; allapot.eredetiMutat = false; rajzol(); });
      lapozo.appendChild(elozo);
      lapozo.appendChild(elem("span", "cetli-szamlalo", `${allapot.index + 1}/${cetlik.length}`));
      lapozo.appendChild(kovetkezo);
      fej.appendChild(lapozo);
    }
    kartya.appendChild(fej);

    const kuldo = feladoNev(c, tartalomNyelv, uiLang);
    const meta = [t(uiLang, "note.from", { name: kuldo }), datum(c.createdAt, uiLang)].filter(Boolean).join(" · ");
    kartya.appendChild(elem("div", "cetli-meta", meta));

    // A fordítás az alap, ha van; az "Eredeti" gombbal váltható (ugyanúgy, mint az üzeneteknél).
    const fordithato = Boolean(c.translatedText);
    const eredetiLatszik = fordithato && allapot.eredetiMutat;
    const megjelenitett = fordithato && !eredetiLatszik ? c.translatedText : c.text;
    const szoveg = elem("div", "cetli-szoveg", megjelenitett);
    const nyelv = fordithato && !eredetiLatszik ? c.translatedLang : c.sourceLang;
    if (nyelv) szoveg.setAttribute("lang", nyelv);
    kartya.appendChild(szoveg);

    if (fordithato) {
      const valto = elem("button", "cetli-valto", t(uiLang, eredetiLatszik ? "message.showTranslation" : "message.showOriginal"));
      valto.type = "button";
      valto.setAttribute("aria-pressed", eredetiLatszik ? "true" : "false");
      valto.addEventListener("click", () => { allapot.eredetiMutat = !allapot.eredetiMutat; rajzol(); });
      kartya.appendChild(valto);
    }

    if (allapot.hiba) kartya.appendChild(elem("div", "cetli-hiba", allapot.hiba));

    const gombok = elem("div", "cetli-gombok");
    const tarolo = elem("button", "cetli-megtart", t(uiLang, "note.later"));
    tarolo.type = "button";
    tarolo.addEventListener("click", bezar);
    const eldob = elem("button", "cetli-eldob", t(uiLang, "note.discard"));
    eldob.type = "button";
    eldob.disabled = allapot.foglalt;
    eldob.addEventListener("click", async () => {
      if (allapot.foglalt) return;
      allapot.foglalt = true;
      allapot.hiba = null;
      rajzol();
      try {
        await allapot.onDobd(c.noteId);
        // A lista a Firestore-figyelőn át frissül (frissit()); addig is tegyük félre az eldobottat,
        // hogy a gomb ne hagyja a már eldobott cetlit a képernyőn.
        allapot.cetlik = allapot.cetlik.filter((x) => x.noteId !== c.noteId);
        allapot.eredetiMutat = false;
      } catch (err) {
        log.error("[cetli] az eldobás nem sikerült:", err);
        allapot.hiba = t(uiLang, "note.discardFailed");
      } finally {
        allapot.foglalt = false;
        rajzol();
      }
    });
    gombok.appendChild(tarolo);
    gombok.appendChild(eldob);
    kartya.appendChild(gombok);
    kartya.appendChild(elem("div", "cetli-tipp", t(uiLang, "note.hint")));
  }

  function frissit(uj) {
    const eddigi = allapot.cetlik[allapot.index];
    allapot = { ...allapot, ...uj, hiba: null };
    // Ha van kért (kijelölt) cetli, azt mutatjuk; különben marad az eddigi, ha még létezik.
    const cel = uj.kijelolt || (eddigi && eddigi.noteId);
    const i = cel ? keres(cel) : -1;
    if (i >= 0) { if (!eddigi || eddigi.noteId !== cel) allapot.eredetiMutat = false; allapot.index = i; }
    else allapot.index = Math.min(allapot.index, Math.max(allapot.cetlik.length - 1, 0));
    rajzol();
  }

  function bezar() {
    document.removeEventListener("keydown", billentyu);
    gyoker.remove();
    if (olvaso && olvaso.gyoker === gyoker) olvaso = null;
  }
  function billentyu(e) { if (e.key === "Escape") bezar(); }
  // A hátterre kattintva a cetli bezárul (de megmarad - csak az "Eldobom" törli).
  gyoker.addEventListener("click", (e) => { if (e.target === gyoker) bezar(); });
  document.addEventListener("keydown", billentyu);

  document.body.appendChild(gyoker);
  olvaso = { gyoker, bezar, frissit };
  frissit(p);
  return olvaso;
}

// ---------------------------------------------------------------------------
// ÍRÓ ABLAK
// ---------------------------------------------------------------------------

let iro = null;

export function cetliIroNyitva() {
  return iro !== null;
}

export function zarjBeCetliIrot() {
  if (iro) iro.bezar();
}

/**
 * @param {object} p
 * @param {string} p.uiLang
 * @param {string} p.cimzettNev a címzett megjelenített neve
 * @param {(szoveg: string) => Promise<void>} p.onKuld sikeresen visszatér, vagy dob (a dobott hiba .message-e látszik, ha van)
 * @param {(err: any) => string|null} [p.hibaSzoveg] a hibából megjeleníthető szöveg (alap: általános felirat)
 */
export function nyissCetliIrot({ uiLang, cimzettNev, onKuld, hibaSzoveg = () => null }) {
  zarjBeCetliIrot();

  const gyoker = elem("div", "cetli-hatter");
  gyoker.setAttribute("role", "dialog");
  const kartya = elem("div", "cetli-kartya cetli-iro");
  gyoker.appendChild(kartya);

  kartya.appendChild(elem("div", "cetli-cim", t(uiLang, "note.composeTitle", { name: cimzettNev })));
  const mezo = document.createElement("textarea");
  mezo.className = "cetli-mezo";
  mezo.rows = 5;
  mezo.maxLength = CETLI_MAX_KARAKTER * 2; // a pontos (kódpont-alapú) korlátot az ellenőrzés adja, nem a maxlength
  mezo.placeholder = t(uiLang, "note.placeholder");
  mezo.setAttribute("aria-label", t(uiLang, "note.placeholder"));
  kartya.appendChild(mezo);

  const szamlalo = elem("div", "cetli-szamlalo-sor");
  kartya.appendChild(szamlalo);
  const uzenet = elem("div", "cetli-hiba");
  uzenet.hidden = true;
  kartya.appendChild(uzenet);

  const gombok = elem("div", "cetli-gombok");
  const megse = elem("button", "cetli-megtart", t(uiLang, "message.editCancel"));
  megse.type = "button";
  const kuld = elem("button", "cetli-eldob cetli-kuld", t(uiLang, "note.send"));
  kuld.type = "button";
  gombok.appendChild(megse);
  gombok.appendChild(kuld);
  kartya.appendChild(gombok);
  kartya.appendChild(elem("div", "cetli-tipp", t(uiLang, "note.hint")));

  const hossz = () => [...mezo.value.trim()].length;
  function frissitSzamlalo() {
    const n = hossz();
    szamlalo.textContent = t(uiLang, "note.counter", { n, max: CETLI_MAX_KARAKTER });
    szamlalo.classList.toggle("tul-hosszu", n > CETLI_MAX_KARAKTER);
    kuld.disabled = n === 0 || n > CETLI_MAX_KARAKTER || foglalt;
  }
  let foglalt = false;
  mezo.addEventListener("input", () => { uzenet.hidden = true; frissitSzamlalo(); });

  async function kuldes() {
    const n = hossz();
    if (foglalt || n === 0 || n > CETLI_MAX_KARAKTER) return;
    foglalt = true;
    uzenet.hidden = true;
    frissitSzamlalo();
    mezo.disabled = true;
    try {
      await onKuld(mezo.value.trim());
      // Sikeres küldés: a visszajelzés rövid, az ablak bezárul.
      kartya.textContent = "";
      kartya.appendChild(elem("div", "cetli-kesz", t(uiLang, "note.sent")));
      setTimeout(bezar, 900);
    } catch (err) {
      log.error("[cetli] a küldés nem sikerült:", err);
      uzenet.textContent = hibaSzoveg(err) || t(uiLang, "note.sendFailed");
      uzenet.hidden = false;
      foglalt = false;
      mezo.disabled = false;
      frissitSzamlalo();
    }
  }
  kuld.addEventListener("click", kuldes);
  megse.addEventListener("click", () => bezar());

  function bezar() {
    document.removeEventListener("keydown", billentyu);
    gyoker.remove();
    if (iro && iro.gyoker === gyoker) iro = null;
  }
  function billentyu(e) {
    if (e.key === "Escape" && !foglalt) bezar();
    else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); kuldes(); }
  }
  document.addEventListener("keydown", billentyu);

  document.body.appendChild(gyoker);
  frissitSzamlalo();
  mezo.focus();
  iro = { gyoker, bezar };
  return iro;
}
